import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import type {
  ComputedToggleBlockStateType,
  ToggleBlockStateType,
  ToggleComponentType,
} from '@unifyapps/defs/types/blocks';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { Stack } from '@unifyapps/ui/components/Stack';
import { memo } from 'react';
import { Toggle as BaseToggle } from '@unifyapps/ui/components/Toggle';
import { Typography } from '@unifyapps/ui/components/Typography';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import { useGetAddOnsStyle } from '../common/fields/BooleanField/hooks/useGetAddOnsStyle';
import { useSetDefaultValue } from '../common/fields/BooleanField/hooks/useSetDefaultValue';

function Toggle({
  attributesAndEventListeners,
  events,
  computedBlockState,
  blockState,
  updateBlockState,
  dndRef,
  dataSourceIds,
  getGlobalStoreContext,
  block,
}: BlockComponentProps<ToggleComponentType, ToggleBlockStateType, ComputedToggleBlockStateType>) {
  const { emitEvent } = useBlockEvents(events);
  const { content, appearance, value } = computedBlockState;
  const { label, description, help, defaultValue } = content;
  const { size, color } = appearance;

  const { neumorphicStyle } = useCustomStyles({ customCSS: block.additional?.customCSS });
  const { labelStyles, descriptionStyles, helpStyles } = useGetAddOnsStyle(content.addOns);

  const { resolvedValue } = useSetDefaultValue<ToggleBlockStateType>({
    defaultValue,
    updateBlockState,
    dataSourceIds,
    getGlobalStoreContext,
    value,
    uncomputedDefaultValue: blockState.content.defaultValue,
  });

  const onChange = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.checked;
    updateBlockState((draft) => {
      draft.value = nextValue;
    });

    void emitEvent({
      eventType: EventTypeEnum.OnChange,
      actionContext: { value: nextValue },
    });
  });

  return (
    <Stack {...attributesAndEventListeners} className="gap-y-sm" ref={dndRef}>
      <Stack className="gap-xxs" direction="column">
        <Stack alignItems="center" className="gap-md" direction="row">
          <BaseToggle
            checked={resolvedValue}
            color={color}
            disabled={appearance.disabled}
            neumorphicStyle={neumorphicStyle}
            onChange={onChange}
            size={size}
          />
          {label ? (
            <Typography
              className={labelStyles.color}
              variant={labelStyles.variant}
              weight={labelStyles.weight}
            >
              {label}
            </Typography>
          ) : null}
        </Stack>
        {description ? (
          <Typography
            className={descriptionStyles.color}
            variant={descriptionStyles.variant}
            weight={descriptionStyles.weight}
          >
            {description}
          </Typography>
        ) : null}
      </Stack>
      {help ? (
        <Typography
          className={helpStyles.color}
          variant={helpStyles.variant}
          weight={helpStyles.weight}
        >
          {help}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default memo(Toggle);
